import { useIsFeatureActive } from '@sortlist-frontend/feature-flags';
import { Monitor } from '@sortlist-frontend/mlm/server';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { assertNonEmptyString, HttpBadRequest, HttpException, HttpNotFound } from '@sortlist-frontend/utils';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import { Fragment, useRef } from 'react';

import { PageHead } from '_components/base/head';
import { PublicGoogleTagManager } from '_components/common';
import { Public404Page } from '_components/error/public-404-page';
import { LayoutPagesRouter } from '_components/layout/LayoutPagesRouter';
import { HomepageJsonld, PageSeoTag } from '_components/SEO';
import { cacheConfig } from '_config/cache.config';
import { extractDomainRelevantInfoFromHeaders } from '_config/domain.config';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { isSerializedError, SerializedError, serializeError } from '_core/error/error-serializer';
import {
  cacheKey,
  getPublicApiRequestLink,
  PUBLIC_API_FIELDS,
  PublicApiRepo,
  publicApiRepo,
  useGetPage,
} from '_core/repos/public-api.repo';
import { baseSsrPrefetchQueries } from '_core/utils/public-api';
import { getServerSideTranslations } from '_core/utils/ssr';
import { toPublicQueryString } from '_core/utils/url/publicQuery';
import { homeConfig } from '_features/home/home.config';
import HomePage from '_features/home/home.page';
import { Agencies } from '_types/public-api';

// Code-split the flag-gated Home Hub so its tree (and framer-motion) is only bundled/loaded when the
// `home-hub-redesign` flag is active, keeping it out of the default `/home` first-load bundle.
const HomeHubPage = dynamic(async () => (await import('_features/HomeHub')).HomeHubPage);
const LayoutPagesRouterHub = dynamic(
  async () => (await import('_components/layout/LayoutPagesRouterHub')).LayoutPagesRouterHub,
);

const DOMAIN_CONFIG_ERROR_STATUSES = [400, 404, 410];

type Prop = {
  canonical: string;
  query: string;
  origin?: string;
  error: SerializedError | null;
  locale?: string;
  url?: string;
  dehydratedState?: DehydratedState;
};

export default function HomeRoute(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <AppProviders dehydratedState={props.dehydratedState}>
      <Route {...props} />
    </AppProviders>
  );
}

const Route = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { canonical, query, origin = '', error, locale = 'en', url = '' } = props;
  const scrollableCtn = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const { data: ssrData } = useGetPage(url);
  const { isActive: isHubActive } = useIsFeatureActive('home-hub-redesign');

  const { metadata, indexable, frontend_type } = ssrData?.data?.attributes || {};

  if (error && isSerializedError(error)) {
    const { status } = error;
    if (DOMAIN_CONFIG_ERROR_STATUSES.includes(status as number))
      return <Public404Page message={t('common:pageErrors.occured')} locale={locale} />;
  }

  PublicApiRepo.publicApiUrl = url;
  PublicApiRepo.publicApiNavBarUrl = url;

  if (ssrData) {
    return (
      <Fragment>
        <PageHead showAlternates={true} canonical={canonical} query={query} origin={origin} />
        <PublicAppContextProvider
          canonical={canonical}
          origin={origin}
          locale={locale}
          briefingOptions={{
            page: frontend_type || '',
          }}>
          <PageSeoTag
            title={isHubActive ? t('home:hub.seo.title') : metadata?.title_tag}
            description={isHubActive ? t('home:hub.seo.description') : metadata?.metadescription}
            nextSeoProps={{
              nofollow: !indexable,
              noindex: !indexable,
            }}
            canonical={canonical}
          />
          <HomepageJsonld isHub={isHubActive} />
          <PublicGoogleTagManager />
          {isHubActive ? (
            <LayoutPagesRouterHub scrollableCtn={scrollableCtn} showSubnav>
              <HomeHubPage />
            </LayoutPagesRouterHub>
          ) : (
            <LayoutPagesRouter scrollableCtn={scrollableCtn} showSubnav>
              <HomePage />
            </LayoutPagesRouter>
          )}
        </PublicAppContextProvider>
      </Fragment>
    );
  }
  return <Public404Page message={t('common:pageErrors.occured')} locale={locale} />;
};

export const getServerSideProps: GetServerSideProps<Prop> = async (context) => {
  let error: SerializedError | null = null;
  const { i18nNamespaces } = homeConfig;
  const queryClient = new QueryClient();

  let domainRelevantInfo;
  try {
    domainRelevantInfo = extractDomainRelevantInfoFromHeaders(context.req);
  } catch (e) {
    if (e instanceof HttpException) {
      context.res.statusCode = e.status;
      error = serializeError(e);
    } else {
      Monitor.captureException(e instanceof Error ? e : new Error('Unexpected SSR error in home domain config'), {
        extra: { url: context.resolvedUrl },
        tags: { context: 'getServerSideProps' },
      });
      throw e;
    }

    return {
      props: {
        error,
        canonical: '',
        query: '',
        ...(await getServerSideTranslations('en', i18nNamespaces)),
      },
    };
  }

  const { canonical, origin, locale } = domainRelevantInfo;
  const queryParams = toPublicQueryString(context.query);

  assertNonEmptyString(origin, new HttpBadRequest({ message: 'Missing [origin] parameter' }));

  const url = getPublicApiRequestLink(origin, locale, context.resolvedUrl);

  let organicAgencies;
  let pageResponse;
  try {
    const pagePromise = publicApiRepo.getPage({ page: url });
    const agenciesPromise = publicApiRepo.getAgencies({
      page: url,
      section: 'organic',
      sort: 'position',
      number: '1',
      limit: 200,
      fieldsNeeded: PUBLIC_API_FIELDS.homeAgencyCard,
    });
    const reviewsPromise = publicApiRepo.getReviews();
    const baseSsrPromise = baseSsrPrefetchQueries(origin, locale, context.resolvedUrl, queryClient, true);

    [pageResponse, organicAgencies] = await Promise.all([pagePromise, agenciesPromise, reviewsPromise, baseSsrPromise]);

    queryClient.setQueryData([cacheKey.page(url)], pageResponse);
    queryClient.setQueryData([cacheKey.agencies(url, 'organic')], organicAgencies);
    queryClient.setQueryData([cacheKey.reviews], await reviewsPromise);
  } catch (e: unknown) {
    if (e instanceof HttpException) {
      if (e.status !== 404) {
        throw e;
      }

      context.res.statusCode = 404;
      error = serializeError(e);
    } else {
      Monitor.captureException(e instanceof Error ? e : new Error('Unexpected SSR error in home page'), {
        extra: { url: context.resolvedUrl },
        tags: { context: 'getServerSideProps' },
      });
      throw e;
    }
  }

  if (error == null && pageResponse == null) {
    context.res.statusCode = 404;
    error = serializeError(new HttpNotFound({ message: 'Homepage not found', url }));
  }

  if (!context.res.headersSent) {
    context.res.setHeader('Cache-Control', cacheConfig.pages.home?.httpCacheControl || '');
  }

  const agencySlugsForPage = ((organicAgencies as Agencies)?.included ?? [])
    .map((agency) => agency.attributes.slug)
    .filter((slug: string) => slug != null);
  const cacheTags = [...new Set(agencySlugsForPage.flat())];

  context.res.setHeader('X-Cache-Tag', cacheTags.join(','));

  return {
    props: {
      canonical,
      query: queryParams,
      origin,
      locale,
      error,
      url,
      dehydratedState: dehydrate(queryClient),
      ...(await getServerSideTranslations(locale, i18nNamespaces)),
    },
  };
};
